import {
  Badge,
  Button,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useGetUsers } from "../../../hooks/query/user-administration";
import { constructQueryParamsString, showErrorToast, showSuccessToast } from "../../../utils";
import { useState } from "react";
import { MULTIPLE_ANSWERS_MARKER } from "../../ISSB/IssbDataInput/components/tools/BulkActions";
import * as React from "react";
import { Plus, X } from "@phosphor-icons/react";
import DataTableV2 from "../../../components/DataTable/DateTableV2";
import { MultiSelect } from "../../../components";
import { LabelText } from "../../../components/text/TextTypes";
import { issbKeys, useChangeOwner, useUpdateEntityUsers } from "../../../hooks/query";
import { hideAppSpinner, showAppSpinner } from "../../../context/Store/slices/appSpinnerSlice";
import { useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import { sortByLabel } from "../../../utils/userSort";

const EntityUserModal = ({ entity, isOpen, onClose }) => {
  const [allUsersList, setAllUsersList] = useState([]);
  const [userList, setUserList] = useState(entity?.users ?? []);

  const dispatch = useDispatch();
  const toast = useToast();
  const queryClient = useQueryClient();

  const getUsersQueryParamsString = () => {
    return constructQueryParamsString({
      page: 0,
      size: 1000,
    });
  };

  useGetUsers(getUsersQueryParamsString(), {
    onSuccess: (data) => {
      const users = sortByLabel(
        data.page?.content?.map((user) => {
          return {
            value: user?.email,
            label: user?.email,
          };
        }),
        (option) => option.label
      );
      setAllUsersList(users);
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
  });

  const onAddUser = (user) => {
    if (!userList.includes(user.value)) {
      setUserList([...userList, user.value]);
    }
  };

  const onRemoveUser = (row) => {
    const user = row?.original;
    setUserList(userList.filter((u) => u !== user));
  };

  const { mutate: updateEntityUsers } = useUpdateEntityUsers({
    onMutate: () => dispatch(showAppSpinner()),
    onError: (err) => {
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
    },
    onSettled: () => dispatch(hideAppSpinner()),
    onSuccess: () => {
      queryClient.invalidateQueries(issbKeys.entity());
      showSuccessToast(toast, "Successfully saved users");
    },
  });

  const handleSubmit = () => {
    const dataToUpdate = {
      entityId: entity.id,
      users: userList,
    };
    updateEntityUsers(dataToUpdate);
  };

  const columns = [
    {
      header: "Email",
      accessorKey: "email",
      cell: (data) => {
        return data.row.original;
      },
    },
    {
      header: "Actions",
      accessorKey: "actions",
      cell: (data) => {
        return (
          <IconButton
            variant="link"
            color="red"
            icon={<X size={18} />}
            aria-label="Remove"
            onClick={() => onRemoveUser(data.row)}
          />
        );
      },
    },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent bg="pureWhite">
        <ModalHeader>Manage Users</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Flex justify="flex-end" my={2}>
            <Menu>
              <MenuButton
                as={Button}
                size="s"
                p={2}
                varient="outline"
                width="fit-content"
                rightIcon={<Plus size={14} weight="bold" />}
              >
                Add
              </MenuButton>
              <MenuList maxHeight="250px" overflowY="auto">
                {allUsersList?.map((user) => (
                  <MenuItem fontSize="body.s" key={user.value} onClick={() => onAddUser(user)}>
                    {user.label}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </Flex>

          <DataTableV2 size="sm" data={userList} columns={columns} enableSorting={false} showPaginator={false} />
        </ModalBody>

        <ModalFooter>
          <Flex direction="column" width="100%" gap={2} my={4}>
            <Button variant="primary" onClick={() => handleSubmit()}>
              Save
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EntityUserModal;
